import React, { useState, useRef, useEffect } from "react";
import { ECOSYSTEM_PRODUCTS, EcosystemProduct } from "../data/ecosystemData";

interface AppsLauncherDropdownProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AppsLauncherDropdown: React.FC<AppsLauncherDropdownProps> = ({
  isOpen,
  onClose,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close when clicking outside
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (
        isOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const filteredApps = ECOSYSTEM_PRODUCTS.filter((product) =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div
      ref={dropdownRef}
      className="homepage1-launcher-dropdown"
      id="apps-launcher-dropdown"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="homepage1-dropdown-header">
        <span className="homepage1-dropdown-title">indexx.ai Apps</span>
        <span className="homepage1-dropdown-badge">
          {ECOSYSTEM_PRODUCTS.length} Products
        </span>
      </div>

      <input
        type="text"
        className="homepage1-launcher-search"
        placeholder="Search ecosystem apps..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        autoFocus
      />

      {filteredApps.length === 0 && (
        <div className="homepage1-launcher-empty">
          No apps match &ldquo;{searchTerm}&rdquo;
        </div>
      )}

      <div className="homepage1-launcher-grid">
        {filteredApps.map((product: EcosystemProduct) => (
          <a
            key={product.id}
            href={product.url.startsWith("http") ? product.url : `#${product.id}`}
            target={product.url.startsWith("http") ? "_blank" : "_self"}
            rel={product.url.startsWith("http") ? "noopener noreferrer" : undefined}
            className="homepage1-launcher-item"
            title={`${product.name} - ${product.category}`}
            onClick={() => {
              if (!product.url.startsWith("http")) {
                const el = document.getElementById(product.id);
                if (el) {
                  el.scrollIntoView({ behavior: "smooth" });
                }
              }
              onClose();
            }}
          >
            {product.isSoon && (
              <span className="homepage1-launcher-soon-tag">SOON</span>
            )}
            <div className="homepage1-launcher-icon">
              <img
                src={product.image}
                alt={`${product.name} logo`}
                className="homepage1-launcher-icon-img"
                loading="lazy"
              />
            </div>
            <span className="homepage1-launcher-name">{product.name}</span>
          </a>
        ))}
      </div>
    </div>
  );
};

export default AppsLauncherDropdown;
